/**
 * InstallPrompt.tsx — "Add to Home Screen" install prompt for PWA.
 *
 * Behavior:
 *   - Listens for the `beforeinstallprompt` browser event (Chrome/Edge/Android)
 *   - Shows a dismissible banner with an "Install" button
 *   - Remembers dismissal in localStorage so it doesn't nag on every visit
 *   - After a successful install, hides the banner permanently
 *
 * Browser support: Chrome, Edge, Samsung Internet, some Android browsers.
 * Safari/iOS shows nothing (iOS uses manual "Add to Home Screen" flow).
 */

import { useEffect, useState } from 'react';

// The BeforeInstallPromptEvent is not in standard lib.dom
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISSED_KEY = 'kapi:pwa-install-dismissed';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Hide if already installed
    window.addEventListener('appinstalled', () => {
      setVisible(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-800 text-white text-sm rounded-lg px-4 py-3 shadow-xl max-w-sm w-[calc(100vw-2rem)]">
      <span className="text-2xl select-none">📲</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-tight">Instalar Kapi POS</p>
        <p className="text-slate-300 text-xs leading-tight mt-0.5">Acceso rápido desde tu pantalla de inicio</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => void handleInstall()}
          className="px-3 py-1 bg-blue-500 hover:bg-blue-400 text-white text-xs font-medium rounded transition-colors"
        >
          Instalar
        </button>
        <button
          onClick={handleDismiss}
          className="px-2 py-1 text-slate-400 hover:text-white text-xs rounded transition-colors"
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
